// apps/admin/src/components/datatable/SavedViewsDropdown.tsx
//
// Phase 8.1 Wave 2 Task 07 — Saved Views UI.
//
// vg-binding: CONTEXT:decisions=P8.D-287,P8.D-289
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: API-CONTRACTS:POST_/api/v1/admin/saved-views
//
// Lists views from `useSavedViews(tableKey)`, lets the user save the current
// `DataTableState` snapshot, restore one, or delete one. Mutation errors are
// shown via `useToast` per Interface Standards
// (error.user_message ?? error.message).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { X } from 'lucide-react';
import { Button, useToast } from '@printway/ui';
import {
  projectSavedViewToState,
  savedViewErrorMessage,
  useSavedViews,
  type SavedView,
} from '../../hooks/useSavedViews.js';
import type { DataTableState } from '../../hooks/useDataTableState.js';
import { useTranslation } from '../../i18n/useTranslation.js';

export interface SavedViewsDropdownProps {
  /** Stable id used for storage + saved-view scoping (e.g. "orders.list"). */
  tableKey: string;
  /** Snapshot of current state — captured at save time. */
  currentState: Pick<DataTableState, 'filters' | 'sort' | 'columnVisibility' | 'pageSize'>;
  /** Restore an existing view. */
  onApply: (view: SavedView) => void;
  className?: string;
  /** Disable network round-trip — useful for offline / preview. */
  localOnly?: boolean;
}

export function SavedViewsDropdown(props: SavedViewsDropdownProps): ReactNode {
  const { tableKey, currentState, onApply, className, localOnly = false } = props;
  const { views, isLoading, create, remove } = useSavedViews({
    tableKey,
    localOnly,
  });
  const toast = useToast();
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSave = useCallback(async () => {
    // Prompt for view name. Window.prompt is fine for v1 — does
    // not mandate a modal; future iteration can upgrade.
    const name =
      typeof window !== 'undefined'
        ? window.prompt('Save current view as…')
        : null;
    const trimmed = (name ?? '').trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({ name: trimmed, state: currentState });
      toast.toast({
        variant: 'success',
        title: 'Saved view created',
        description: trimmed,
      });
    } catch (err) {
      toast.toast({
        variant: 'error',
        title: 'Could not save view',
        description: savedViewErrorMessage(err),
      });
    }
  }, [create, currentState, toast]);

  const handleApply = useCallback(
    (view: SavedView) => {
      onApply(view);
      setOpen(false);
    },
    [onApply],
  );

  const handleDelete = useCallback(
    async (view: SavedView) => {
      if (
        typeof window !== 'undefined' &&
        !window.confirm(t('datatable.deleteSavedViewConfirm', { name: view.name }))
      ) {
        return;
      }
      try {
        await remove.mutateAsync({ id: view.id });
        toast.toast({
          variant: 'success',
          title: 'Saved view deleted',
          description: view.name,
        });
      } catch (err) {
        toast.toast({
          variant: 'error',
          title: 'Could not delete view',
          description: savedViewErrorMessage(err),
        });
      }
    },
    [remove, toast, t],
  );

  return (
    <div
      ref={rootRef}
      className={['relative inline-block', className].filter(Boolean).join(' ')}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="datatable-saved-views"
      >
        Saved views ({views.length})
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={t('datatable.savedViewsAria')}
          className="absolute right-0 z-30 mt-1 min-w-[260px] rounded-md border border-pw-border bg-pw-bg p-2 shadow-md"
        >
          <div className="mb-2 flex items-center justify-between gap-2 border-b border-pw-border pb-2">
            <span className="text-xs font-medium text-pw-text-muted">
              Saved views
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={create.isPending}
              data-testid="datatable-saved-views-save"
            >
              {create.isPending ? 'Saving…' : 'Save current'}
            </Button>
          </div>
          {isLoading && (
            <div className="py-2 text-center text-xs text-pw-text-muted">
              Loading…
            </div>
          )}
          {!isLoading && views.length === 0 && (
            <div className="py-2 text-center text-xs text-pw-text-muted">
              No saved views yet.
            </div>
          )}
          {views.length > 0 && (
            <ul className="flex flex-col gap-1 text-sm">
              {views.map((view) => (
                <li
                  key={view.id}
                  className="flex items-center gap-2 rounded px-2 py-1 hover:bg-pw-surface"
                >
                  <button
                    type="button"
                    className="flex-1 truncate text-left text-pw-text"
                    onClick={() => handleApply(view)}
                    title={view.name}
                    data-testid={`datatable-saved-view-${view.id}`}
                  >
                    {view.name}
                  </button>
                  <button
                    type="button"
                    className="text-xs text-pw-text-muted hover:text-pw-text"
                    onClick={() => handleDelete(view)}
                    disabled={remove.isPending}
                    aria-label={`Delete ${view.name}`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
SavedViewsDropdown.displayName = 'AdminSavedViewsDropdown';

export { projectSavedViewToState };
